import { FormControlLabel, MenuItem } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Formik } from 'formik';
import { useMemo } from 'react';
import cookie from 'react-cookies';
import { useRecoilValueLoadable } from 'recoil';
import { LabelledOutline } from 'src/common/final-form-material-ui';
import * as Yup from 'yup';

import Axios from '../../../common/axiosInterceptor';
import {
  FormikSelect,
  FormikSwitch,
  FormikTextField,
} from '../../../common/formik-material-ui';
import '../../../common/formik-material-ui/isValidNumber';
import { trackSaveAddBlockedNumber } from '../../../common/userTracking';
import { user } from '../../../recoil/atoms/user';
import { countries } from '../countries';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';

import { ModalWithStyles } from './ModalWithStyles';

const useStyles = makeStyles((theme) => ({
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    width: '335px',
  },
  modal: {
    paddingBottom: 0,
    maxHeight: 'calc(100vh - 210px)',
    overflowY: 'auto',
    width: '445px',
    marginLeft: '38%',
  },
}));

export function AddBlockedNumberModal({
  show,
  onHide,
  setRefetchToggleForBlockedNumbers,
}) {
  const userAtom = useRecoilValueLoadable(user);
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const classes = useStyles();
  const blockedNumberSchema = useMemo(
    () =>
      Yup.object({
        number: Yup.string()
          .required('Required')
          .isValidNumber('Must be valid number for countrycode'),
      }),
    []
  );

  const showBlockedNumbersSnackbar = (message, severity, animationDuration) => {
    enqueueSnackbarWithStyles({
      message: message,
      alertProps: { severity: severity },
      animationDuration: animationDuration,
    });
  };

  const handleBlockedNumbersSubmit = async (values, actions) => {
    const {
      inboundCalls,
      outboundCalls,
      inboundSMS,
      outboundSMS,
      number,
      countrycode,
    } = values;
    // {"businessid":995,"apikey":"d8810712-df40-11e4-9ccc-22000ac88c3a","number":"18058079440","outboundsms":true,"inboundsms":true,"inboundcalls":true,"outboundcalls":true}
    const hasMoreThan10 = number.split(',', 11).length > 10;
    const data = JSON.stringify({
      businessid: userAtom.contents?.businessid,
      apikey: userAtom.contents?.apikey,
      outboundsms: outboundSMS,
      number,
      countrycode,
      inboundsms: inboundSMS,
      inboundcalls: inboundCalls,
      outboundcalls: outboundCalls,
      email: userAtom.contents?.user?.email,
    });
    const url = `${process.env.VITE_APP_API_DOMAIN}/${process.env.VITE_APP_API_STAGE}/blockedNumbers/numbers`;
    const accessToken = cookie.load('accessToken');
    try {
      const response = await Axios({
        method: 'post',
        url,
        headers: {
          authorization: accessToken,
        },
        data,
      });
      setRefetchToggleForBlockedNumbers({});
      if (response.data.body.result === 'created') {
        showBlockedNumbersSnackbar(
          response.data.body?.msg,
          'success',
          hasMoreThan10 ? 0 : 5000
        );
      }
      if (
        response.data.body.result === 'updated' &&
        response.data.body.success
      ) {
        showBlockedNumbersSnackbar(
          `Successfully Updated Blocked Numbers`,
          'success',
          hasMoreThan10 ? 0 : 5000
        );
      }
      onHide();
      actions.resetForm({});
    } catch (error) {
      showBlockedNumbersSnackbar(`Error updating Blocked Numbers`, 'error', 0);
    }
  };

  return (
    <Formik
      validationSchema={blockedNumberSchema}
      onSubmit={handleBlockedNumbersSubmit}
      //   validationSchema={}
      enableReinitialize={true}
      initialValues={{
        number: '',
        inboundCalls: false,
        outboundCalls: false,
        inboundSMS: false,
        outboundSMS: false,
      }}
    >
      {(formik) => (
        <>
          <form
            // className="form form-label-right"
            onSubmit={formik.handleSubmit}
            noValidate
          >
            <ModalWithStyles
              dataPup={'save-add-blocked-numbers'}
              className={classes.modal}
              onSubmit={() => {
                trackSaveAddBlockedNumber();
                formik.handleSubmit();
              }}
              title="Block A Number"
              show={show}
              onHide={onHide}
              isSubmitting={formik.isSubmitting}
              size="xs"
              disableSubmit={!formik.isValid}
            >
              <div className="row">
                <div
                  style={{ marginLeft: '10.5px', marginRight: '10.5px' }}
                  className="row"
                >
                  <FormikSelect
                    dataPup="add-blocked-numbers-select-country"
                    name="countrycode"
                    className={classes.field}
                    formik={formik}
                    fullWidth
                    label="Country"
                    size="small"
                    variant="outlined"
                  >
                    {countries.map((country, index) => (
                      <MenuItem key={index} value={country.code}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                </div>
                <div
                  style={{ marginLeft: '10.5px', marginRight: '10.5px' }}
                  className="row"
                >
                  <FormikTextField
                    dataPup="add-blocked-numbers-external-number-field"
                    id="standard-basic"
                    label="External Numbers"
                    helperText="Use E.164 phone number format (No Plus)"
                    name="number"
                    className={classes.field}
                    formik={formik}
                    size="small"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={12}
                  />
                </div>
                <div
                  style={{ marginLeft: '10.5px', marginRight: '10.5px' }}
                  className="row"
                >
                  <LabelledOutline label="Calls" customPadding="5px 8px">
                    <FormControlLabel
                      control={
                        <FormikSwitch
                          dataPup="add-blocked-numbers-inbound-calls-switch"
                          name="inboundCalls"
                          formik={formik}
                          color="primary"
                          type="checkbox"
                        />
                      }
                      label="Inbound Calls"
                    />

                    <FormControlLabel
                      control={
                        <FormikSwitch
                          dataPup="add-blocked-numbers-outbound-calls-switch"
                          name="outboundCalls"
                          formik={formik}
                          color="primary"
                          type="checkbox"
                        />
                      }
                      label="Outbound Calls"
                    />
                  </LabelledOutline>
                </div>
                <div
                  style={{
                    marginLeft: '10.5px',
                    marginRight: '10.5px',
                    marginTop: '10px',
                  }}
                  className="row"
                >
                  <LabelledOutline label="SMS" customPadding="5px 8px">
                    <FormControlLabel
                      control={
                        <FormikSwitch
                          dataPup="add-blocked-numbers-inbound-sms-switch"
                          name="inboundSMS"
                          formik={formik}
                          color="primary"
                          type="checkbox"
                        />
                      }
                      label={'Inbound SMS' + '\xa0\xa0'}
                    />
                    <FormControlLabel
                      control={
                        <FormikSwitch
                          dataPup="add-blocked-numbers-outbound-sms-switch"
                          name="outboundSMS"
                          formik={formik}
                          color="primary"
                          type="checkbox"
                        />
                      }
                      label={'Outbound SMS' + '\xa0'}
                    />
                  </LabelledOutline>
                </div>
              </div>

              <br />
            </ModalWithStyles>
          </form>
        </>
      )}
    </Formik>
  );
}
